import { api, qs } from './client.js';
import type { Paginated } from './types.js';

/**
 * 서버: modules/network (파트너 광고 DB).
 *
 * 광고 현황 - 원본 /xadm/report_network_test_frame(_main).php · report_network.php ·
 * partner_ajax.php (admin_partner_click).
 * 리퍼러 목록·통계 · 광고 사이트 · 계정정보 - 원본 /xadm/report_network_referer.php ·
 * report_network_referer_group.php · report_network_referer_site.php ·
 * report_network_adinfo.php (ALK_REFERER_network · ALK_REFERER_network_group ·
 * ALK_network_site · ALK_network_adinfo). 이 넷은 읽기 전용이다.
 */

export interface NetworkGroup {
  key: string;
  title: string;
  /** 그룹 기본 단가(달러) */
  cpc: number;
  /** 제목만 있는 구분선 - 고를 수 없다. */
  separator?: true;
}

export interface NetworkRow {
  seq: number;
  /** YYYY-MM-DD */
  date: string;
  /** 0=일 ~ 6=토 */
  weekday: number;
  groupname: string;
  clickSum: number;
  clickp: number;
  clickm: number;
  /** 랜딩1·2 만 - 팝언더 대비 비율(×100 안 한 값, 원본 표기). */
  randingRateP: number | null;
  randingRateM: number | null;
  ordernum: number;
  price: number;
  /** 광고비(원), 반올림 전 */
  adCost: number;
  /** 수익율(%) - null 이면 원본처럼 '1,000%' */
  profitRate: number | null;
  clickRate: number;
  joinnum: number;
  /** 저장된 지출비용(달러) */
  cpc: number;
  /** 지출비용 칸에 보이는 값(0 이면 그룹 기본 단가) */
  cpcDisplay: number;
  imp: number;
  memo: string;
}

export interface NetworkTotals {
  clickSum: number;
  clickp: number;
  clickm: number;
  ordernum: number;
  price: number;
  adCost: number;
  profitRate: number | null;
  joinnum: number;
  cpc: number;
  imp: number;
}

export interface NetworkReport {
  group: { key: string; title: string; defaultCpc: number };
  from: string;
  to: string;
  weekday: number | null;
  randing: boolean;
  usdToKrw: number;
  rows: NetworkRow[];
  totals: NetworkTotals;
}

/** 재계산 지표 = admin_partner_click 컬럼 (server network.schemas.ts 의 METRICS 와 같다). */
export const RECALC_METRICS = ['clickp', 'clickm', 'ordernum', 'price', 'joinnum'] as const;
export type RecalcMetric = (typeof RECALC_METRICS)[number];

export type EditableField = 'cpc' | 'imp' | 'memo';

export interface RecalcResultRow {
  seq: number;
  date: string;
  groupname: string;
  values: Partial<Record<RecalcMetric, number>>;
  /** 실제로 저장한 지표 - 0 으로 계산된 것은 원본처럼 저장하지 않는다. */
  saved: RecalcMetric[];
}

export interface NetworkReportParams {
  group: string;
  from?: string;
  to?: string;
  weekday?: number;
}

/** 그 리퍼러 도메인이 '광고 사이트'에 등록돼 있을 때 딸려오는 분류. */
export interface NetworkSiteRef {
  uid: number;
  hosttype: string | null;
  bannertype: string | null;
}

/** 리퍼러 목록 한 줄 (ALK_REFERER_network). */
export interface RefererRow {
  uid: number;
  yearmonth: string;
  partner: string | null;
  refhost: string | null;
  referer: string | null;
  param: string | null;
  ip: string | null;
  /** 'p'(PC) · 'm'(모바일) */
  agent: string | null;
  regdate: string | null;
  site: NetworkSiteRef | null;
}

/** 리퍼러 통계 한 줄 (ALK_REFERER_network_group - 미리 세어 둔 집계). */
export interface RefererStatsRow {
  uid: number;
  yearmonth: string;
  partner: string | null;
  refhost: string | null;
  agent: string | null;
  cnt: number;
  site: NetworkSiteRef | null;
}

/** 광고 사이트 한 줄 (ALK_network_site). */
export interface AdSiteRow {
  uid: number;
  refhost: string | null;
  hosttype: string | null;
  bannertypes: string[];
  adsites: string | null;
  partnerCodes: string | null;
  memo: string | null;
  regdate: string | null;
}

/**
 * 광고 계정정보 한 줄 (ALK_network_adinfo).
 * 가입비번(join_pw)은 서버가 조회하지 않으므로 여기에도 없다.
 */
export interface AdAccountRow {
  seq: number;
  siteurl: string | null;
  stats: string | null;
  nameEn: string | null;
  nameKr: string | null;
  joinEmail: string | null;
  joinId: string | null;
  adTypes: string[];
  payTypes: string[];
  memo: string | null;
  regdate: string | null;
}

/** 서버가 실제로 적용한 달·기간을 같이 준다 (비워 보내면 서버가 기본값을 넣는다). */
export interface RefererList extends Paginated<RefererRow> {
  searchYm: string;
  from: string;
  to: string;
}

export interface RefererStatsList extends Paginated<RefererStatsRow> {
  searchYm: string;
}

interface ListParams {
  page?: number;
  perPage?: number;
  sort?: string;
  dir?: string;
}

export interface RefererParams extends ListParams {
  /** 'YYYYMM' */
  searchYm?: string;
  from?: string;
  to?: string;
  /** 파트너 아이디 - 정확히 일치해야 걸린다 (원본과 같다). */
  partner?: string;
  /** 리퍼러 도메인 - 부분 일치. */
  refhost?: string;
}

export interface RefererStatsParams extends ListParams {
  searchYm?: string;
  partner?: string;
  refhost?: string;
}

export interface KeywordParams extends ListParams {
  q?: string;
}

export const networkApi = {
  groups: () => api.get<{ groups: NetworkGroup[] }>('/network/groups'),
  report: (p: NetworkReportParams) => api.get<NetworkReport>(`/network/report${qs({ ...p })}`),
  ensureDates: (group: string) => api.post<{ created: string[] }>('/network/report/ensure-dates', { group }),
  recalc: (seqs: number[], metrics?: RecalcMetric[]) =>
    api.post<{ rows: RecalcResultRow[] }>('/network/rows/recalc', metrics ? { seqs, metrics } : { seqs }),
  save: (seq: number, field: EditableField, value: string) =>
    api.patch<{ seq: number; field: EditableField; value: string | number }>(`/network/rows/${seq}`, { field, value }),
  addPreviousDay: (group: string, date: string) =>
    api.post<{ created: boolean; date: string }>('/network/rows/previous-day', { group, date }),

  referers: (p: RefererParams) => api.get<RefererList>(`/network/referers${qs({ ...p })}`),
  refererMonths: () => api.get<{ months: string[] }>('/network/referers/months'),
  refererStats: (p: RefererStatsParams) => api.get<RefererStatsList>(`/network/referer-stats${qs({ ...p })}`),
  refererStatsMonths: () => api.get<{ months: string[] }>('/network/referer-stats/months'),
  sites: (p: KeywordParams) => api.get<Paginated<AdSiteRow>>(`/network/sites${qs({ ...p })}`),
  adAccounts: (p: KeywordParams) => api.get<Paginated<AdAccountRow>>(`/network/adinfo${qs({ ...p })}`),
};
